// Inventory Engine admin — Phase 1 Foundation + Phase 2 Goods Inward (Oct 2026).
//
// Phase 1: enable/configure inventory, create items with a base unit and
// packaging, link catalog products, manage locations, post opening
// balances / adjustments / status changes / reversals, and view stock and
// movement history. Phase 2 (components/inventory/GoodsInwardPanels.jsx):
// suppliers and supplier items, goods inward (GRN) with lots, and the
// lots & expiry view.
//
// Every number shown here comes from the server as a string; the page
// never does quantity arithmetic. Every stock-changing form carries an
// idempotency key minted once per attempt, so a double click or retry can
// never change stock twice. Sales-linked holds, deductions and restocks
// are managed on the Sales tab (components/inventory/SalesPanel.jsx).

import React, { useCallback, useEffect, useMemo, useState } from "react";
import DashboardLayout from "../components/DashboardLayout";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Boxes, MapPin, Package, Settings, History, ArrowLeftRight, Link2, Loader2, Truck, PackageCheck, CalendarClock, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import { formatEquivalents, inventoryErrorMessage, levelLabel, lotDateLabel, newOperationKey } from "../lib/inventory";
import { Field, Select, fail } from "../components/inventory/fields";
import { GoodsInwardPanel, LotsPanel, SuppliersPanel } from "../components/inventory/GoodsInwardPanels";
import { SalesPanel } from "../components/inventory/SalesPanel";

const TABS = [
  { key: "setup", label: "Setup", icon: Settings },
  { key: "items", label: "Items & Packaging", icon: Package },
  { key: "locations", label: "Locations", icon: MapPin },
  { key: "suppliers", label: "Suppliers", icon: Truck },
  { key: "receiving", label: "Goods Inward", icon: PackageCheck },
  { key: "operations", label: "Stock Operations", icon: ArrowLeftRight },
  { key: "stock", label: "Stock on Hand", icon: Boxes },
  { key: "lots", label: "Lots & Expiry", icon: CalendarClock },
  { key: "sales", label: "Sales", icon: ShoppingCart },
  { key: "movements", label: "Movement History", icon: History },
];

// ---------------------------------------------------------------- Setup

function SetupPanel({ settings, vocab, onSaved }) {
  const [form, setForm] = useState(settings);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm(settings); }, [settings]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/inventory/settings", {
        enabled: Boolean(form.enabled),
        document_prefix: form.document_prefix,
        negative_stock_policy: form.negative_stock_policy,
        default_inventory_policy: form.default_inventory_policy,
        near_expiry_days: form.near_expiry_days ? parseInt(form.near_expiry_days, 10) : undefined,
        sales_integration_enabled: Boolean(form.sales_integration_enabled),
        cash_reservation_hold_minutes: form.cash_reservation_hold_minutes ? parseInt(form.cash_reservation_hold_minutes, 10) : undefined,
      });
      toast.success("Inventory settings saved");
      onSaved(data);
    } catch (e) { fail(e, "Could not save settings"); } finally { setSaving(false); }
  };

  return (
    <Card className="p-5 space-y-4" data-testid="inventory-setup-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-semibold">Inventory for this business</h2>
          <p className="text-sm text-muted-foreground">
            Opt-in. Existing products stay untracked until you link them to an inventory item. Checkout and orders are not affected in this phase.
          </p>
        </div>
        <Switch checked={Boolean(form.enabled)} onCheckedChange={(v) => setForm({ ...form, enabled: v })} data-testid="inventory-enabled-switch" />
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        <Field label="Document prefix (e.g. RIC → RIC-MOV-2026-000001)">
          <Input value={form.document_prefix || ""} maxLength={6}
            onChange={(e) => setForm({ ...form, document_prefix: e.target.value.toUpperCase() })} data-testid="inventory-prefix" />
        </Field>
        <Field label="Negative stock (staff operations)">
          <Select value={form.negative_stock_policy} onChange={(v) => setForm({ ...form, negative_stock_policy: v })}
            options={(vocab?.negative_stock_policies || []).map((p) => ({ value: p, label: p === "BLOCK" ? "Block (default)" : "Allow only with an explicit, reasoned override" }))} />
        </Field>
        <Field label="Default sales policy (applies from Phase 3)">
          <Select value={form.default_inventory_policy} onChange={(v) => setForm({ ...form, default_inventory_policy: v })}
            options={(vocab?.inventory_policies || []).map((p) => ({ value: p, label: p }))} />
        </Field>
        <Field label="Near-expiry warning window (days)">
          <Input value={form.near_expiry_days ?? 7} inputMode="numeric"
            onChange={(e) => setForm({ ...form, near_expiry_days: e.target.value.replace(/[^0-9]/g, "") })} data-testid="inventory-near-expiry-days" />
        </Field>
      </div>
      <div className="rounded-md border p-3 space-y-3" data-testid="inventory-sales-integration">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="font-medium text-sm">Sales integration (deduct stock when orders are paid)</div>
            <p className="text-xs text-muted-foreground">
              Off by default. When on, checkout holds stock for products linked to an inventory item, refuses orders that
              cannot be covered, and deducts stock once payment is confirmed. Unlinked products are never affected.
              Customers only ever see In stock / Out of stock.
            </p>
          </div>
          <Switch checked={Boolean(form.sales_integration_enabled)} disabled={!form.enabled}
            onCheckedChange={(v) => setForm({ ...form, sales_integration_enabled: v })} data-testid="inventory-sales-switch" />
        </div>
        <Field label="Cash / Pay-at-counter hold (minutes before an unpaid hold is released)">
          <Input className="w-32" value={form.cash_reservation_hold_minutes ?? 60} inputMode="numeric"
            onChange={(e) => setForm({ ...form, cash_reservation_hold_minutes: e.target.value.replace(/[^0-9]/g, "") })} data-testid="inventory-cash-hold" />
        </Field>
      </div>
      <p className="text-xs text-muted-foreground">Kiosk, online and mobile ordering always block insufficient tracked stock while sales integration is on.</p>
      <Button onClick={save} disabled={saving} data-testid="inventory-settings-save">
        {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Save settings
      </Button>
    </Card>
  );
}

// ---------------------------------------------------------------- Items

function CreateItemForm({ uoms, vocab, onCreated }) {
  const baseOptions = uoms.filter((u) => u.can_be_base_uom).map((u) => ({ value: u.code, label: `${u.code} — ${u.label}` }));
  const packageOptions = uoms.filter((u) => u.dimension === "PACKAGE").map((u) => ({ value: u.code, label: `${u.code} — ${u.label}` }));
  const blank = { item_code: "", name: "", item_class: "goods", base_uom: "EA", packaging: [] };
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);

  const setLevel = (i, patch) => setForm({ ...form, packaging: form.packaging.map((p, j) => (j === i ? { ...p, ...patch } : p)) });
  const addLevel = () => setForm({ ...form, packaging: [...form.packaging, { key: `L${form.packaging.length + 1}`, uom_code: "BX", contains_qty: "", contains: form.packaging.length ? `L${form.packaging.length}` : "BASE", label: "" }] });

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/inventory/items", {
        item_code: form.item_code, name: form.name, item_class: form.item_class, base_uom: form.base_uom,
        packaging_levels: form.packaging.map((p) => ({ key: p.key, uom_code: p.uom_code, contains_qty: String(p.contains_qty), contains: p.contains, label: p.label || undefined })),
      });
      toast.success(`Created ${data.item_code}`);
      setForm(blank);
      onCreated(data);
    } catch (e) { fail(e, "Could not create item"); } finally { setBusy(false); }
  };

  return (
    <Card className="p-4 space-y-3" data-testid="inventory-create-item">
      <h3 className="font-semibold">New inventory item</h3>
      <div className="grid sm:grid-cols-4 gap-3">
        <Field label="Item code"><Input value={form.item_code} onChange={(e) => setForm({ ...form, item_code: e.target.value })} data-testid="item-code" /></Field>
        <Field label="Name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="item-name" /></Field>
        <Field label="Class"><Select value={form.item_class} onChange={(v) => setForm({ ...form, item_class: v })} options={(vocab?.item_classes || ["goods"]).map((c) => ({ value: c, label: c }))} /></Field>
        <Field label="Base unit (what you count)"><Select value={form.base_uom} onChange={(v) => setForm({ ...form, base_uom: v })} options={baseOptions} testId="item-base-uom" /></Field>
      </div>
      <div className="space-y-2">
        <div className="text-sm font-medium">Packaging (largest last). Each level contains a quantity of a smaller level.</div>
        {form.packaging.map((p, i) => (
          <div key={p.key} className="grid grid-cols-2 sm:grid-cols-5 gap-2 items-end">
            <Field label={`Level ${p.key}`}><Select value={p.uom_code} onChange={(v) => setLevel(i, { uom_code: v })} options={packageOptions} /></Field>
            <Field label="Label"><Input value={p.label} onChange={(e) => setLevel(i, { label: e.target.value })} placeholder="Box of 10" /></Field>
            <Field label="Contains qty"><Input value={p.contains_qty} onChange={(e) => setLevel(i, { contains_qty: e.target.value })} inputMode="decimal" /></Field>
            <Field label="of"><Select value={p.contains} onChange={(v) => setLevel(i, { contains: v })}
              options={[{ value: "BASE", label: `${form.base_uom} (base)` }, ...form.packaging.slice(0, i).map((x) => ({ value: x.key, label: `${x.key} (${x.uom_code})` }))]} /></Field>
            <Button variant="outline" onClick={() => setForm({ ...form, packaging: form.packaging.filter((_, j) => j !== i) })}>Remove</Button>
          </div>
        ))}
        <Button variant="outline" size="sm" onClick={addLevel} data-testid="item-add-level">+ Packaging level</Button>
      </div>
      <Button onClick={submit} disabled={busy || !form.item_code || !form.name} data-testid="item-create-submit">Create item</Button>
    </Card>
  );
}

function TrackingPolicy({ item, vocab, onSaved }) {
  const pick = (it) => ({ expiry_tracking: it.expiry_tracking || "none", lot_capture: it.lot_capture || "optional",
    quarantine_on_receipt: Boolean(it.quarantine_on_receipt), allocation_policy: it.allocation_policy || "" });
  const [form, setForm] = useState(pick(item));
  useEffect(() => { setForm(pick(item)); }, [item]);
  const save = async () => {
    try {
      await api.patch(`/inventory/items/${item.id}`, { ...form, allocation_policy: form.allocation_policy || null });
      toast.success("Lot & expiry policy saved");
      onSaved();
    } catch (e) { fail(e, "Could not save policy"); }
  };
  return (
    <div data-testid="item-tracking-policy">
      <div className="text-sm font-medium mb-1">Lots & expiry (applies to future goods inward)</div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 items-end">
        <Field label="Date tracking"><Select value={form.expiry_tracking} onChange={(v) => setForm({ ...form, expiry_tracking: v })} options={(vocab?.expiry_tracking || ["none"]).map((v) => ({ value: v, label: v === "expiry" ? "Expiry date (hard)" : v === "best_before" ? "Best-before date" : "None" }))} testId="item-expiry-tracking" /></Field>
        <Field label="Supplier lot"><Select value={form.lot_capture} onChange={(v) => setForm({ ...form, lot_capture: v })} options={(vocab?.lot_capture || ["optional"]).map((v) => ({ value: v, label: v }))} /></Field>
        <Field label="Picking order"><Select value={form.allocation_policy} onChange={(v) => setForm({ ...form, allocation_policy: v })} options={(vocab?.allocation_policies || []).map((v) => ({ value: v, label: v }))} placeholder="Automatic (FEFO if dated)" /></Field>
        <label className="flex items-center gap-2 text-sm h-9"><Switch checked={form.quarantine_on_receipt} onCheckedChange={(v) => setForm({ ...form, quarantine_on_receipt: v })} />Inspect on receipt (quarantine)</label>
        <Button size="sm" onClick={save}>Save policy</Button>
      </div>
    </div>
  );
}

function ItemDetail({ itemId, uoms, vocab, onChanged }) {
  const [item, setItem] = useState(null);
  const [level, setLevel] = useState({ uom_code: "CS", contains_qty: "", contains_level_id: "", supersedes_level_id: "", label: "" });
  const [convert, setConvert] = useState({ quantity: "1", packaging_level_id: "" });
  const [preview, setPreview] = useState(null);
  const [catalogs, setCatalogs] = useState([]);
  const [catalogId, setCatalogId] = useState("");
  const [products, setProducts] = useState([]);
  const [link, setLink] = useState({ product_id: "", packaging_level_id: "", qty_per_sale: "1" });

  const load = useCallback(async () => {
    try { setItem((await api.get(`/inventory/items/${itemId}`)).data); } catch (e) { fail(e, "Could not load item"); }
  }, [itemId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/retail/catalogs").then((r) => setCatalogs(r.data?.catalogs || [])).catch(() => setCatalogs([]));
  }, []);
  useEffect(() => {
    if (!catalogId) { setProducts([]); return; }
    api.get(`/retail/catalogs/${catalogId}/products`, { params: { limit: 500 } })
      .then((r) => setProducts(r.data?.items || [])).catch((e) => fail(e, "Could not load products"));
  }, [catalogId]);

  if (!item) return <Card className="p-4 text-sm text-muted-foreground">Loading item…</Card>;
  const active = item.packaging_levels.filter((l) => l.status === "active");
  const levelOptions = active.map((l) => ({ value: l.level_id, label: levelLabel(l) }));
  const packageOptions = uoms.filter((u) => u.dimension === "PACKAGE").map((u) => ({ value: u.code, label: u.code }));

  const call = async (fn, ok) => {
    try { await fn(); toast.success(ok); await load(); onChanged(); } catch (e) { fail(e, "Request failed"); }
  };

  return (
    <Card className="p-4 space-y-5" data-testid="inventory-item-detail">
      <div>
        <h3 className="font-semibold">{item.item_code} — {item.name}</h3>
        <p className="text-xs text-muted-foreground">Base unit {item.base_uom} · precision {item.base_precision} · {item.has_movements ? "stock has moved (base unit locked)" : "no movements yet"}</p>
      </div>

      <TrackingPolicy item={item} vocab={vocab} onSaved={async () => { await load(); onChanged(); }} />

      <div>
        <div className="text-sm font-medium mb-1">Packaging levels</div>
        <Table>
          <TableHeader><TableRow><TableHead>Unit</TableHead><TableHead>Label</TableHead><TableHead>Contains</TableHead><TableHead>= base</TableHead><TableHead>State</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>
            {item.packaging_levels.map((l) => {
              const child = item.packaging_levels.find((x) => x.level_id === l.contains_level_id);
              return (
                <TableRow key={l.level_id} data-testid={`level-row-${l.uom_code}`}>
                  <TableCell>{l.uom_code}</TableCell>
                  <TableCell>{l.label}</TableCell>
                  <TableCell>{l.is_base ? "—" : `${l.contains_qty} × ${child?.uom_code || "?"}`}</TableCell>
                  <TableCell>{l.factor_to_base} {item.base_uom}</TableCell>
                  <TableCell className="space-x-1">
                    <Badge variant={l.status === "active" ? "secondary" : "outline"}>{l.status}</Badge>
                    {l.locked && <Badge variant="outline">locked</Badge>}
                  </TableCell>
                  <TableCell>
                    {!l.is_base && l.status === "active" && (
                      <Button size="sm" variant="ghost" onClick={() => call(() => api.post(`/inventory/items/${item.id}/packaging-levels/${l.level_id}/retire`), "Level retired")}>Retire</Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 items-end mt-2">
          <Field label="Unit"><Select value={level.uom_code} onChange={(v) => setLevel({ ...level, uom_code: v })} options={packageOptions} /></Field>
          <Field label="Label"><Input value={level.label} onChange={(e) => setLevel({ ...level, label: e.target.value })} /></Field>
          <Field label="Contains qty"><Input value={level.contains_qty} onChange={(e) => setLevel({ ...level, contains_qty: e.target.value })} /></Field>
          <Field label="of level"><Select value={level.contains_level_id} onChange={(v) => setLevel({ ...level, contains_level_id: v })} options={levelOptions} placeholder="Base unit" /></Field>
          <Field label="Replaces (optional)"><Select value={level.supersedes_level_id} onChange={(v) => setLevel({ ...level, supersedes_level_id: v })} options={levelOptions.filter((o) => !active.find((l) => l.level_id === o.value)?.is_base)} placeholder="—" /></Field>
          <Button onClick={() => call(() => api.post(`/inventory/items/${item.id}/packaging-levels`, {
            uom_code: level.uom_code, contains_qty: level.contains_qty, label: level.label || undefined,
            contains_level_id: level.contains_level_id || undefined, supersedes_level_id: level.supersedes_level_id || undefined,
          }), "Packaging level added")} data-testid="level-add-submit">Add level</Button>
        </div>
        <p className="text-xs text-muted-foreground mt-1">A level used by any stock movement is locked forever; to change what a box holds, add a new level that replaces it.</p>
      </div>

      <div>
        <div className="text-sm font-medium mb-1">Conversion check</div>
        <div className="flex flex-wrap gap-2 items-end">
          <Field label="Quantity"><Input value={convert.quantity} onChange={(e) => setConvert({ ...convert, quantity: e.target.value })} className="w-28" /></Field>
          <Field label="In"><Select value={convert.packaging_level_id} onChange={(v) => setConvert({ ...convert, packaging_level_id: v })} options={levelOptions} placeholder="Base unit" /></Field>
          <Button variant="outline" onClick={async () => {
            try {
              const { data } = await api.post(`/inventory/items/${item.id}/convert`, { quantity: convert.quantity, packaging_level_id: convert.packaging_level_id || undefined });
              setPreview(data);
            } catch (e) { fail(e, "Conversion failed"); }
          }} data-testid="convert-submit">Convert</Button>
          {preview && <span className="text-sm" data-testid="convert-result">{preview.qty_in_uom} {preview.uom_code} = <strong>{preview.base_qty} {preview.base_uom}</strong> ({formatEquivalents(preview.equivalents)})</span>}
        </div>
      </div>

      <div>
        <div className="text-sm font-medium mb-1 flex items-center gap-1"><Link2 className="w-4 h-4" />Linked catalog products</div>
        {item.links.length === 0 ? <p className="text-sm text-muted-foreground">No catalog products are linked. Unlinked products are not inventory tracked.</p> : (
          <Table>
            <TableHeader><TableRow><TableHead>Product</TableHead><TableHead>Sells as</TableHead><TableHead>Qty per sale</TableHead><TableHead>Policy</TableHead><TableHead /></TableRow></TableHeader>
            <TableBody>
              {item.links.map((l) => (
                <TableRow key={l.id}>
                  <TableCell>{l.product_name || l.product_id}</TableCell>
                  <TableCell>{levelLabel(item.packaging_levels.find((x) => x.level_id === l.packaging_level_id))}</TableCell>
                  <TableCell>{l.qty_per_sale}</TableCell>
                  <TableCell>{l.inventory_policy || "inherit"}</TableCell>
                  <TableCell><Button size="sm" variant="ghost" onClick={() => call(() => api.post(`/inventory/product-links/${l.id}/unlink`), "Unlinked")}>Unlink</Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 items-end mt-2">
          <Field label="Catalog"><Select value={catalogId} onChange={setCatalogId} options={catalogs.map((c) => ({ value: c.id, label: c.name }))} placeholder="Choose…" /></Field>
          <Field label="Product"><Select value={link.product_id} onChange={(v) => setLink({ ...link, product_id: v })} options={products.map((p) => ({ value: p.id, label: p.sku ? `${p.name} (${p.sku})` : p.name }))} placeholder="Choose…" testId="link-product" /></Field>
          <Field label="Sells as"><Select value={link.packaging_level_id} onChange={(v) => setLink({ ...link, packaging_level_id: v })} options={levelOptions} placeholder="Base unit" /></Field>
          <Field label="Qty per sale"><Input value={link.qty_per_sale} onChange={(e) => setLink({ ...link, qty_per_sale: e.target.value })} /></Field>
          <Button disabled={!link.product_id} onClick={() => call(() => api.post("/inventory/product-links", {
            product_id: link.product_id, item_id: item.id, packaging_level_id: link.packaging_level_id || undefined, qty_per_sale: link.qty_per_sale,
          }), "Product linked")} data-testid="link-submit">Link product</Button>
        </div>
      </div>
    </Card>
  );
}

function ItemsPanel({ items, uoms, vocab, onChanged }) {
  const [selected, setSelected] = useState(null);
  return (
    <div className="space-y-4" data-testid="inventory-items-panel">
      <CreateItemForm uoms={uoms} vocab={vocab} onCreated={(it) => { onChanged(); setSelected(it.id); }} />
      <Card className="p-0 overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Base</TableHead><TableHead>Packaging</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
          <TableBody>
            {items.map((it) => (
              <TableRow key={it.id} className="cursor-pointer" onClick={() => setSelected(it.id)} data-testid={`item-row-${it.item_code}`}>
                <TableCell className="font-mono">{it.item_code}</TableCell>
                <TableCell>{it.name}</TableCell>
                <TableCell>{it.base_uom}</TableCell>
                <TableCell>{it.packaging_levels.filter((l) => !l.is_base && l.status === "active").map((l) => `${l.uom_code}=${l.factor_to_base}`).join(", ") || "—"}</TableCell>
                <TableCell><Badge variant="secondary">{it.status}</Badge></TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={5} className="text-sm text-muted-foreground">No inventory items yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
      {selected && <ItemDetail key={selected} itemId={selected} uoms={uoms} vocab={vocab} onChanged={onChanged} />}
    </div>
  );
}

// ---------------------------------------------------------------- Locations

function LocationsPanel({ locations, vocab, onChanged }) {
  const [form, setForm] = useState({ type: "aisle", code: "", name: "", parent_id: "" });
  const submit = async () => {
    try {
      await api.post("/inventory/locations", { ...form, parent_id: form.type === "site" ? undefined : form.parent_id || undefined });
      toast.success("Location created");
      setForm({ ...form, code: "", name: "" });
      onChanged();
    } catch (e) { fail(e, "Could not create location"); }
  };
  const toggle = async (loc) => {
    try { await api.patch(`/inventory/locations/${loc.id}`, { active: !loc.active }); onChanged(); } catch (e) { fail(e, "Could not update location"); }
  };
  return (
    <div className="space-y-4" data-testid="inventory-locations-panel">
      <Card className="p-4 space-y-3">
        <h3 className="font-semibold">New location</h3>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 items-end">
          <Field label="Type"><Select value={form.type} onChange={(v) => setForm({ ...form, type: v })} options={(vocab?.location_types || []).map((t) => ({ value: t, label: t }))} /></Field>
          <Field label="Inside"><Select value={form.parent_id} onChange={(v) => setForm({ ...form, parent_id: v })} options={locations.filter((l) => l.active).map((l) => ({ value: l.id, label: l.path }))} placeholder={form.type === "site" ? "(site is a root)" : "Choose…"} /></Field>
          <Field label="Code"><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="A3" /></Field>
          <Field label="Name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Aisle 3" /></Field>
          <Button onClick={submit} disabled={!form.code || !form.name} data-testid="location-create-submit">Create</Button>
        </div>
      </Card>
      <Card className="p-0 overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Path</TableHead><TableHead>Name</TableHead><TableHead>Type</TableHead><TableHead>Holds stock</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>
            {locations.map((l) => (
              <TableRow key={l.id}>
                <TableCell className="font-mono" style={{ paddingLeft: `${0.75 + l.depth * 1.25}rem` }}>{l.path}</TableCell>
                <TableCell>{l.name} {l.is_default_site && <Badge variant="outline">default site</Badge>} {l.is_default_stock && <Badge variant="outline">default stock</Badge>}</TableCell>
                <TableCell>{l.type}</TableCell>
                <TableCell>{l.stock_holding ? "yes" : "no"}</TableCell>
                <TableCell>{!l.is_default_site && !l.is_default_stock && <Button size="sm" variant="ghost" onClick={() => toggle(l)}>{l.active ? "Deactivate" : "Reactivate"}</Button>}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------- Operations

function OperationsPanel({ items, locations, vocab, settings, onPosted }) {
  const [kind, setKind] = useState("opening");
  const [form, setForm] = useState({ item_id: "", location_id: "", lot_id: "", quantity: "", packaging_level_id: "", direction: "decrease", reason_code: "damage", from_status: "AVAILABLE", to_status: "QUARANTINE", notes: "", override_reason: "" });
  const [lots, setLots] = useState([]);
  const [opKey, setOpKey] = useState(newOperationKey);
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);
  const item = items.find((i) => i.id === form.item_id);
  const stockLocations = locations.filter((l) => l.active && l.stock_holding);
  const set = (patch) => { setForm({ ...form, ...patch }); setOpKey(newOperationKey()); };
  useEffect(() => {
    if (!form.item_id) { setLots([]); return; }
    api.get("/inventory/lots", { params: { item_id: form.item_id, status: "active", include_empty: false } })
      .then((r) => setLots(r.data.lots || [])).catch(() => setLots([]));
  }, [form.item_id]);

  const reasons = kind === "adjust"
    ? Object.entries(vocab?.adjustment_reasons || {}).filter(([, dirs]) => dirs.includes(form.direction)).map(([r]) => r)
    : vocab?.status_change_reasons || [];

  const submit = async () => {
    setBusy(true);
    const base = { idempotency_key: opKey, item_id: form.item_id, location_id: form.location_id || undefined, quantity: form.quantity,
      packaging_level_id: form.packaging_level_id || undefined, notes: form.notes || undefined };
    try {
      let res;
      if (kind === "opening") res = await api.post("/inventory/operations/opening-balance", base);
      else if (kind === "adjust") {
        res = await api.post("/inventory/operations/adjustments", { ...base, lot_id: form.lot_id || undefined, direction: form.direction, reason_code: form.reason_code,
          negative_override: form.override_reason ? { reason: form.override_reason } : undefined });
      } else {
        res = await api.post("/inventory/operations/status-changes", { ...base, lot_id: form.lot_id || undefined, from_status: form.from_status, to_status: form.to_status, reason_code: form.reason_code });
      }
      setLast(res.data);
      toast.success(res.data.outcome === "already_applied" ? "Already posted — no change made" : `Posted ${res.data.operation_no}`);
      setOpKey(newOperationKey());
      onPosted();
    } catch (e) { fail(e, "Operation refused"); } finally { setBusy(false); }
  };

  return (
    <Card className="p-4 space-y-3" data-testid="inventory-operations-panel">
      <div className="flex gap-2 flex-wrap">
        {[["opening", "Opening balance"], ["adjust", "Manual adjustment"], ["status", "Status change"]].map(([k, l]) => (
          <Button key={k} variant={kind === k ? "default" : "outline"} size="sm" onClick={() => { setKind(k); setOpKey(newOperationKey()); setForm({ ...form, reason_code: k === "status" ? "quarantine" : "damage" }); }} data-testid={`op-kind-${k}`}>{l}</Button>
        ))}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Field label="Item"><Select value={form.item_id} onChange={(v) => set({ item_id: v, packaging_level_id: "", lot_id: "" })} options={items.filter((i) => i.status === "active").map((i) => ({ value: i.id, label: `${i.item_code} — ${i.name}` }))} placeholder="Choose…" testId="op-item" /></Field>
        <Field label="Location"><Select value={form.location_id} onChange={(v) => set({ location_id: v })} options={stockLocations.map((l) => ({ value: l.id, label: l.path }))} placeholder="Default stock location" /></Field>
        {kind !== "opening" && (
          <Field label="Lot (stock without a lot if blank)"><Select value={form.lot_id} onChange={(v) => set({ lot_id: v, location_id: "" })} options={lots.map((l) => ({ value: l.id, label: `${l.lot_no} — ${l.on_hand} ${l.base_uom} · ${lotDateLabel(l)}` }))} placeholder="No lot" testId="op-lot" /></Field>
        )}
        <Field label="Quantity"><Input value={form.quantity} onChange={(e) => set({ quantity: e.target.value })} inputMode="decimal" data-testid="op-quantity" /></Field>
        <Field label="Unit"><Select value={form.packaging_level_id} onChange={(v) => set({ packaging_level_id: v })} options={(item?.packaging_levels || []).filter((l) => l.status === "active" && !l.is_base).map((l) => ({ value: l.level_id, label: levelLabel(l) }))} placeholder={item ? `${item.base_uom} (base)` : "Base unit"} testId="op-unit" /></Field>
        {kind === "adjust" && (
          <>
            <Field label="Direction"><Select value={form.direction} onChange={(v) => set({ direction: v, reason_code: v === "increase" ? "found" : "damage" })} options={[{ value: "increase", label: "Increase" }, { value: "decrease", label: "Decrease" }]} /></Field>
            <Field label="Reason"><Select value={form.reason_code} onChange={(v) => set({ reason_code: v })} options={reasons.map((r) => ({ value: r, label: r }))} /></Field>
          </>
        )}
        {kind === "status" && (
          <>
            <Field label="From status"><Select value={form.from_status} onChange={(v) => set({ from_status: v })} options={(vocab?.stock_statuses || []).map((s) => ({ value: s, label: s }))} /></Field>
            <Field label="To status"><Select value={form.to_status} onChange={(v) => set({ to_status: v })} options={(vocab?.stock_statuses || []).map((s) => ({ value: s, label: s }))} /></Field>
            <Field label="Reason"><Select value={form.reason_code} onChange={(v) => set({ reason_code: v })} options={reasons.map((r) => ({ value: r, label: r }))} /></Field>
          </>
        )}
        <Field label="Notes"><Input value={form.notes} onChange={(e) => set({ notes: e.target.value })} /></Field>
        {kind === "adjust" && form.direction === "decrease" && settings.negative_stock_policy !== "BLOCK" && (
          <Field label="Negative-stock override reason (only if going below zero)"><Input value={form.override_reason} onChange={(e) => set({ override_reason: e.target.value })} /></Field>
        )}
      </div>
      <Button onClick={submit} disabled={busy || !form.item_id || !form.quantity} data-testid="op-submit">Post to ledger</Button>
      {last && (
        <div className="text-sm rounded-md bg-muted p-3" data-testid="op-result">
          <div className="font-medium">{last.operation_no} — {last.outcome}</div>
          {last.movements.map((m) => (
            <div key={m.id}>{m.direction === "in" ? "+" : "−"}{m.qty_in_uom} {m.uom_code} × {m.factor_to_base} = {m.base_qty_delta} {m.base_uom} · {m.stock_status} @ {m.location_path} → balance {m.balance_after}</div>
          ))}
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------- Stock & history

function StockPanel({ balances }) {
  return (
    <Card className="p-0 overflow-x-auto" data-testid="inventory-stock-panel">
      <Table>
        <TableHeader><TableRow><TableHead>Item</TableHead><TableHead>Location</TableHead><TableHead>Lot</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Quantity</TableHead></TableRow></TableHeader>
        <TableBody>
          {balances.map((b) => (
            <TableRow key={`${b.item_id}:${b.location_id}:${b.lot_id || "-"}:${b.stock_status}`}>
              <TableCell><span className="font-mono">{b.item_code}</span> {b.item_name}</TableCell>
              <TableCell className="font-mono">{b.location_path}</TableCell>
              <TableCell className="font-mono text-xs">{b.lot_no || "—"}{b.lot_no && <div className="font-sans text-muted-foreground">{lotDateLabel(b)}</div>}</TableCell>
              <TableCell><Badge variant={b.sellable ? "secondary" : "outline"}>{b.stock_status}</Badge></TableCell>
              <TableCell className="text-right">{b.qty} {b.base_uom}</TableCell>
            </TableRow>
          ))}
          {balances.length === 0 && <TableRow><TableCell colSpan={5} className="text-sm text-muted-foreground">No stock recorded yet.</TableCell></TableRow>}
        </TableBody>
      </Table>
    </Card>
  );
}

// Undone by their own documents (receipt void, cancelled-order restock),
// never by the generic reversal.
const NON_REVERSIBLE = new Set(["REVERSAL", "RECEIPT", "SALE", "SALE_REVERSAL"]);

function MovementsPanel({ movements, onReversed }) {
  const reverse = async (m) => {
    const notes = window.prompt(`Reverse ${m.operation_no}? Enter the reason:`);
    if (!notes) return;
    try {
      await api.post(`/inventory/operations/${m.operation_id}/reverse`, { idempotency_key: newOperationKey(), notes });
      toast.success("Reversal posted");
      onReversed();
    } catch (e) { fail(e, "Reversal refused"); }
  };
  return (
    <Card className="p-0 overflow-x-auto" data-testid="inventory-movements-panel">
      <Table>
        <TableHeader><TableRow><TableHead>When</TableHead><TableHead>Operation</TableHead><TableHead>Type / reason</TableHead><TableHead>Item</TableHead><TableHead>Entered</TableHead><TableHead>Base Δ</TableHead><TableHead>Status @ location</TableHead><TableHead>By</TableHead><TableHead /></TableRow></TableHeader>
        <TableBody>
          {movements.map((m) => (
            <TableRow key={m.id}>
              <TableCell className="whitespace-nowrap text-xs">{(m.recorded_at || "").replace("T", " ").slice(0, 19)}</TableCell>
              <TableCell className="font-mono text-xs">{m.operation_no}{m.legs_total > 1 ? ` (${m.leg + 1}/${m.legs_total})` : ""}</TableCell>
              <TableCell className="text-xs">{m.movement_type}<br />{m.reason_code}{m.negative_override && <Badge variant="destructive" className="ml-1">override</Badge>}
                {m.reserved_override && <Badge variant="destructive" className="ml-1">reserved override</Badge>}
                {m.source?.type === "retail_order" && <div className="font-mono text-muted-foreground" data-testid={`movement-order-${m.id}`}>Order {m.source.no || m.source.id}</div>}
                {m.lot_id && <div className="text-muted-foreground">lot</div>}</TableCell>
              <TableCell className="font-mono text-xs">{m.item_code}</TableCell>
              <TableCell className="text-xs">{m.qty_in_uom} {m.uom_code} × {m.factor_to_base}</TableCell>
              <TableCell className="text-xs">{m.base_qty_delta} {m.base_uom}</TableCell>
              <TableCell className="text-xs">{m.stock_status} @ {m.location_path}</TableCell>
              <TableCell className="text-xs">{m.actor?.email}</TableCell>
              <TableCell>{m.leg === 0 && !NON_REVERSIBLE.has(m.movement_type) && <Button size="sm" variant="ghost" onClick={() => reverse(m)}>Reverse</Button>}</TableCell>
            </TableRow>
          ))}
          {movements.length === 0 && <TableRow><TableCell colSpan={9} className="text-sm text-muted-foreground">No movements yet.</TableCell></TableRow>}
        </TableBody>
      </Table>
    </Card>
  );
}

// ---------------------------------------------------------------- Page

export default function InventoryAdminPage() {
  const [tab, setTab] = useState("setup");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [settings, setSettings] = useState(null);
  const [vocab, setVocab] = useState(null);
  const [uoms, setUoms] = useState([]);
  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [balances, setBalances] = useState([]);
  const [movements, setMovements] = useState([]);

  const loadData = useCallback(async () => {
    const [it, loc, st, mv] = await Promise.all([
      api.get("/inventory/items"), api.get("/inventory/locations"),
      api.get("/inventory/stock"), api.get("/inventory/movements", { params: { limit: 200 } }),
    ]);
    setItems(it.data.items || []);
    setLocations(loc.data.locations || []);
    setBalances(st.data.balances || []);
    setMovements(mv.data.movements || []);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, v, u] = await Promise.all([api.get("/inventory/settings"), api.get("/inventory/vocabulary"), api.get("/inventory/uoms")]);
      setSettings(s.data);
      setVocab(v.data);
      setUoms(u.data.uoms || []);
      if (s.data.enabled) await loadData();
    } catch (e) {
      setError(inventoryErrorMessage(e, "Could not load inventory"));
    } finally { setLoading(false); }
  }, [loadData]);

  useEffect(() => { load(); }, [load]);

  const refresh = useCallback(() => { loadData().catch((e) => fail(e, "Could not refresh")); }, [loadData]);
  const visibleTabs = useMemo(() => (settings?.enabled ? TABS : TABS.slice(0, 1)), [settings]);

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="inventory-admin-page">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold">Inventory</h1>
          <p className="text-sm text-muted-foreground">Manage items, packaging, locations, suppliers, goods inward, lots & expiry, stock movements and sales-linked inventory.</p>
        </div>
        {error && <Card className="p-4 text-sm text-destructive" data-testid="inventory-error">{error}</Card>}
        {loading && !settings ? <Card className="p-4 text-sm text-muted-foreground">Loading…</Card> : settings && (
          <>
            <div className="flex flex-wrap gap-2 border-b pb-2">
              {visibleTabs.map(({ key, label, icon: Icon }) => (
                <Button key={key} variant={tab === key ? "default" : "ghost"} size="sm" onClick={() => setTab(key)} data-testid={`inventory-tab-${key}`}>
                  <Icon className="w-4 h-4 mr-1" />{label}
                </Button>
              ))}
            </div>
            {tab === "setup" && <SetupPanel settings={settings} vocab={vocab} onSaved={(s) => { setSettings(s); if (s.enabled) refresh(); }} />}
            {settings.enabled && tab === "items" && <ItemsPanel items={items} uoms={uoms} vocab={vocab} onChanged={refresh} />}
            {settings.enabled && tab === "locations" && <LocationsPanel locations={locations} vocab={vocab} onChanged={refresh} />}
            {settings.enabled && tab === "suppliers" && <SuppliersPanel items={items} onChanged={refresh} />}
            {settings.enabled && tab === "receiving" && <GoodsInwardPanel items={items} locations={locations} vocab={vocab} settings={settings} onChanged={refresh} />}
            {settings.enabled && tab === "lots" && <LotsPanel items={items} settings={settings} />}
            {settings.enabled && tab === "sales" && <SalesPanel settings={settings} onChanged={refresh} />}
            {settings.enabled && tab === "operations" && <OperationsPanel items={items} locations={locations} vocab={vocab} settings={settings} onPosted={refresh} />}
            {settings.enabled && tab === "stock" && <StockPanel balances={balances} />}
            {settings.enabled && tab === "movements" && <MovementsPanel movements={movements} onReversed={refresh} />}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
